import React, { useCallback, useMemo, useState } from 'react';
import useUrlState from '../../../../Hooks/useUrlState';
import styles from './SongCollectionTracker.module.css';
import SelectBox from '../../../../ui/DataEntry/SelectBox/SelectBox';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import {
    getSongCollection,
    getSongCollections,
    GetSongCollectionSongStatsParams,
    getSongCollectionStats,
    GetSongCollectionTiktokAudioStatsParams,
    removeSongFromSongCollection,
    SongCollection,
    SongCollectionStats,
    SongTiktokStats,
    usePaginatedFetch,
} from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import Card from '../../../../ui/Card/Card';
import { Helmet } from 'react-helmet';
import useCollectionSongs from './Songs/useCollectionSongs';
import SearchInput from '../../../../ui/DataEntry/SearchInput/SearchInput';
import Select from '../../../../ui/DataEntry/Select/Select';
import { GenericDropdownOption } from '../../../../App.types';
import SongsTable from './Songs/SongsTable';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import { StylesConfig } from 'react-select';
import SongDetailsModal from '../../../Song/features/SongDetailsModal/SongDetailsModal';
import useSongCollectionTiktokAudios from './TitkokAudios/useSongCollectionTiktokAudios';
import TiktokAudiosTable from './TitkokAudios/TiktokAudiosTable';
import { AudioPlayerProvider } from '../../../AudioPlayer/AudioPlayerContext';
import MetricCard from '../../../../ui/DataDisplay/Statistics/MetricCard/MetricCard';
import MusicNoteIcon from '../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import VideoCameraIcon from '../../../../SharedComponents/svg/Icons/VideoCameraIcon';
import CalendarMonthIcon from '../../../../SharedComponents/svg/Icons/CalendarMonthIcon';
import Button from '../../../../ui/Buttons/Button/Button';
import CreateSongCollectionModal from '../CreateSongCollectionModal/CreateSongCollectionModal';
import Option from '../../../../ui/DataEntry/SelectBox/components/Option/Option';
import { ReactComponent as EditIcon } from '../../../../assets/EditIcon.svg';
import EditSongCollectionModal from '../EditSongCollectionModal/EditSongCollectionModal';
import { showNotification } from '../../../../helpers';
import TiktokAudioModal from '../../../TikTok/audios/features/details/TiktokAudioModal/TiktokAudioModal';

type UrlState = {
    collectionId: number | null;
    songsPage: number;
    songsPageSize: number;
    songsSearch: string;
    songsOrdering: GetSongCollectionSongStatsParams['ordering'];
    songDetails: number | null;
    audiosPage: number;
    audiosPageSize: number;
    audiosSearch: string;
    audiosOrdering: GetSongCollectionTiktokAudioStatsParams['ordering'];
    audioDetails: number | null;
};

const initialUrlState: UrlState = {
    collectionId: null,
    songsOrdering: '-total_video_count_daily_change',
    songsPage: 1,
    songsPageSize: 10,
    songsSearch: '',
    songDetails: null,
    audiosPage: 1,
    audiosPageSize: 10,
    audiosSearch: '',
    audiosOrdering: '-video_count_daily_change',
    audioDetails: null,
};

type SongsOrderingOption = GenericDropdownOption<GetSongCollectionSongStatsParams['ordering']>;
const songsOrderingOptions: SongsOrderingOption[] = [
    { label: 'Total Videos', value: '-total_video_count' },
    { label: 'Daily Growth (# videos)', value: '-total_video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-total_video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-total_video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-total_video_count_weekly_change_relative' },
];

type AudiosOrderingOption = GenericDropdownOption<GetSongCollectionTiktokAudioStatsParams['ordering']>;
const audiosOrderingOptions: AudiosOrderingOption[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const SongCollectionTracker = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);
    const [initialCollectionLoading, setInitialCollectionLoading] = useState(false);
    const [selectedCollection, setSelectedCollection] = useState<SelectBoxOptionWithId>();

    const [isCreateSongCollectionModalOpen, setIsCreateSongCollectionModalOpen] = useState(false);
    const [isEditSongCollectionModalOpen, setIsEditSongCollectionModalOpen] = useState(false);
    const [songCollectionToEdit, setSongCollectionToEdit] = useState<SongCollection | null>(null);
    const [editedCollections, setEditedCollections] = useState<SongCollection[]>([]);

    const pageTitle = useMemo(() => {
        let title = 'Songs | Collections | trnds';

        if (selectedCollection) {
            title = `${selectedCollection.name} | ${title}`;
        }

        return title;
    }, [selectedCollection]);

    const [collectionOptionsPage, setCollectionOptionsPage] = useState(1);
    const [collectionOptionsSearch, setCollectionOptionsSearch] = useState('');
    const {
        data: collections,
        count: collectionsCount,
        loading: collectionsLoading,
        getCurrentPage: getCurrentPageCollections,
        hasNextPage: hasNextPageCollections,
    } = usePaginatedFetch(getSongCollections, {
        page: collectionOptionsPage,
        page_size: 10,
        search: collectionOptionsSearch,
    });

    const selectCollection = useCallback(
        (collection: SelectBoxOptionWithId) => {
            setSelectedCollection(collection);
            setUrlState({ collectionId: collection.id });
        },
        [setUrlState]
    );

    const collectionOptions: SelectBoxOptionWithId[] = useMemo(() => {
        return collections
            .map((c) => editedCollections.find((edited) => edited.id === c.id) ?? c)
            .map((c) => ({
                id: c.id,
                name: c.name,
                picture: c.picture ?? '',
            }));
    }, [collections, editedCollections]);

    useAbortableEffect(
        (signal) => {
            if (urlState.collectionId && !selectedCollection) {
                setInitialCollectionLoading(true);
                getSongCollection(Number(urlState.collectionId), { signal })
                    .then((response) => {
                        if (response.status === 200) {
                            const { data } = response;
                            selectCollection({
                                id: data.id,
                                name: data.name,
                                picture: data.picture ?? '',
                            });
                        }
                    })
                    .catch(() => {})
                    .finally(() => setInitialCollectionLoading(false));

                return;
            }

            if (!urlState.collectionId && !selectedCollection && collectionOptions.length) {
                selectCollection(collectionOptions[0]);
            }
        },
        [urlState.collectionId, selectedCollection, collectionOptions, selectCollection]
    );

    useAbortableEffect(
        (signal) => {
            getCurrentPageCollections({ signal });
        },
        [getCurrentPageCollections]
    );

    const [songsDisplayed, setSongsDisplayed] = useState(0);
    const {
        page: songs,
        count: songsCount,
        loading: songsLoading,
        getCurrentPage: getCurrentSongsPage,
    } = useCollectionSongs(selectedCollection?.id, {
        ordering: (urlState.songsOrdering ?? initialUrlState['songsOrdering']) as UrlState['songsOrdering'],
        search: urlState.songsSearch,
        page_size: Number(urlState.songsPageSize),
        page: Number(urlState.songsPage),
    });

    const [selectedSongRow, setSelectedSongRow] = useState<SongTiktokStats | null>(null);
    const [isSongDetailsModalOpen, setIsSongDetailsModalOpen] = useState(Boolean(urlState.songDetails));

    const [audiosDisplayed, setAudiosDisplayed] = useState(0);
    const {
        data: audios,
        count: audiosCount,
        loading: audiosLoading,
        getCurrentPage: getTiktokAudiosCurrentPage,
    } = useSongCollectionTiktokAudios(selectedCollection?.id, {
        ordering: (urlState.audiosOrdering ?? initialUrlState['audiosOrdering']) as UrlState['audiosOrdering'],
        search: urlState.audiosSearch,
        page_size: Number(urlState.audiosPageSize),
        page: Number(urlState.audiosPage),
    });

    const [isAudioDetailsModalOpen, setIsAudioDetailsModalOpen] = useState(Boolean(urlState.audioDetails));

    const [songCollectionStats, setSongCollectionStats] = useState<SongCollectionStats | null>(null);
    const [songCollectionStatsLoading, setSongCollectionStatsLoading] = useState(false);

    useAbortableEffect(
        (signal) => {
            if (!selectedCollection) {
                return;
            }

            setSongCollectionStatsLoading(true);
            setSongCollectionStats(null);
            getSongCollectionStats(selectedCollection.id, { signal })
                .then((response) => {
                    if (response.status === 200) {
                        setSongCollectionStats(response.data);
                    }
                })
                .catch(() => {})
                .finally(() => setSongCollectionStatsLoading(false));
        },
        [selectedCollection]
    );

    const removeSongFromCollection = useCallback(
        async (songId: number) => {
            if (!selectedCollection) {
                return;
            }

            try {
                const response = await removeSongFromSongCollection(selectedCollection.id, songId);
                if (response.status === 204) {
                    getCurrentSongsPage();
                    getTiktokAudiosCurrentPage();
                    showNotification('Song removed', 'info');
                    return;
                }

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }
            } catch {
                showNotification("Couldn't remove the song", 'error');
            }
        },
        [getCurrentSongsPage, getTiktokAudiosCurrentPage, selectedCollection]
    );

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <Card className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.collectionSelectContainer}>
                        <SelectBox
                            loading={initialCollectionLoading}
                            placeholder="Select collection"
                            value={selectedCollection}
                            onChange={selectCollection}
                            options={collectionOptions}
                            optionsCount={collectionsCount}
                            optionsLoading={collectionsLoading}
                            searchable
                            search={collectionOptionsSearch}
                            setSearch={(search) => {
                                setCollectionOptionsPage(1);
                                setCollectionOptionsSearch(search);
                            }}
                            loadMore={async () => {
                                if (hasNextPageCollections) {
                                    setCollectionOptionsPage((page) => page + 1);
                                }
                            }}
                            renderDropdownBottom={(setShowDropdown) => (
                                <Button
                                    className={styles.createCollectionButton}
                                    onClick={() => {
                                        setShowDropdown(false);
                                        setIsCreateSongCollectionModalOpen(true);
                                    }}
                                >
                                    Create
                                </Button>
                            )}
                            renderItem={(props) => (
                                <Option
                                    {...props}
                                    renderRight={() => (
                                        <Button
                                            className={styles.editCollectionButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.setShowDropdown(false);
                                                const collection =
                                                    editedCollections.find((c) => c.id === props.item?.id) ??
                                                    collections.find((c) => c.id === props.item?.id);

                                                if (!collection) {
                                                    return;
                                                }

                                                setSongCollectionToEdit(collection);
                                                setIsEditSongCollectionModalOpen(true);
                                            }}
                                        >
                                            <EditIcon />
                                        </Button>
                                    )}
                                />
                            )}
                        />

                        <CreateSongCollectionModal
                            isOpen={isCreateSongCollectionModalOpen}
                            onClose={() => setIsCreateSongCollectionModalOpen(false)}
                            onCreated={(collection) => {
                                setIsCreateSongCollectionModalOpen(false);
                                selectCollection({
                                    ...collection,
                                    picture: collection.picture ?? '',
                                });
                            }}
                        />

                        <EditSongCollectionModal
                            isOpen={isEditSongCollectionModalOpen}
                            onClose={() => {
                                setIsEditSongCollectionModalOpen(false);
                                setSongCollectionToEdit(null);
                            }}
                            onUpdated={(collection) => {
                                setIsEditSongCollectionModalOpen(false);
                                setSongCollectionToEdit(null);
                                setEditedCollections((collections) => {
                                    const editedCollection = collections.find((c) => c.id === collection.id);
                                    return editedCollection
                                        ? collections.map((c) => (c.id === editedCollection.id ? editedCollection : c))
                                        : collections.concat(collection);
                                });

                                if (selectedCollection?.id === collection.id) {
                                    selectCollection({
                                        ...collection,
                                        picture: collection.picture ?? '',
                                    });
                                }
                            }}
                            songCollection={songCollectionToEdit}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.statsContainer}>
                        <MetricCard
                            baseColor="#0090FF"
                            loading={songCollectionStatsLoading}
                            value={songCollectionStats?.total_audios_tracked}
                            label="Total Audios Tracked"
                            Icon={MusicNoteIcon}
                        />
                        <MetricCard
                            baseColor="#F7685B"
                            loading={songCollectionStatsLoading}
                            value={songCollectionStats?.total_video_count}
                            label="Total Video Creations"
                            Icon={VideoCameraIcon}
                        />
                        <MetricCard
                            baseColor="#FFA114"
                            loading={songCollectionStatsLoading}
                            value={songCollectionStats?.new_video_count_seven_days}
                            label="creations in the last 7 days"
                            Icon={CalendarMonthIcon}
                        />
                        <MetricCard
                            baseColor="#48D98A"
                            loading={songCollectionStatsLoading}
                            value={songCollectionStats?.new_video_count_one_day}
                            label="Daily Creations"
                            Icon={CalendarMonthIcon}
                        />
                    </div>

                    <div>
                        <h2 className={styles.title}>Songs</h2>
                        <p className={styles.subtitle}>
                            {songsLoading ? (
                                <Skeleton width="10rem" />
                            ) : (
                                `displaying ${songsDisplayed} out of ${songsCount} song${songsCount !== 1 ? 's' : ''}`
                            )}
                        </p>
                    </div>

                    <div className={styles.toolbar}>
                        <SearchInput
                            className={styles.search}
                            value={urlState.songsSearch}
                            onChange={(search) => setUrlState({ songsPage: 1, songsSearch: search })}
                        />

                        <div className={styles.toolbarItem}>
                            <label className={styles.toolbarItemLabel} htmlFor="songsOrdering">
                                Sort By
                            </label>
                            <Select
                                className={styles.orderingSelect}
                                styles={sortingSelectStyles}
                                id="songsOrdering"
                                options={songsOrderingOptions}
                                value={songsOrderingOptions.find((o) => o.value === urlState.songsOrdering)}
                                onChange={(o) =>
                                    setUrlState({ songsOrdering: o?.value ?? initialUrlState['songsOrdering'] })
                                }
                            />
                        </div>
                    </div>

                    <SongsTable
                        loading={songsLoading}
                        ordering={urlState.songsOrdering as GetSongCollectionSongStatsParams['ordering']}
                        data={songs}
                        count={songsCount}
                        page={Number(urlState.songsPage)}
                        setPage={(p) => setUrlState({ songsPage: p })}
                        pageSize={Number(urlState.songsPageSize)}
                        setPageSize={(ps) => setUrlState({ songsPageSize: ps ?? initialUrlState['songsPageSize'] })}
                        onPaginationChange={(state) => setSongsDisplayed(state.pageRows?.length)}
                        onRowClick={(row) => {
                            setSelectedSongRow(row);
                            setUrlState({ songDetails: row.song.id });
                            setIsSongDetailsModalOpen(true);
                        }}
                        onRemoveSongClicked={(row) => removeSongFromCollection(row.song.id)}
                    />

                    <SongDetailsModal
                        isOpen={isSongDetailsModalOpen}
                        onClose={() => {
                            setIsSongDetailsModalOpen(false);
                            setSelectedSongRow(null);
                            setUrlState({ songDetails: null });
                        }}
                        songId={typeof urlState.songDetails === 'string' ? Number(urlState.songDetails) : null}
                        songTiktokStats={selectedSongRow}
                    />

                    <div>
                        <h2 className={styles.title}>Top Audios</h2>
                        <p className={styles.subtitle}>
                            {songsLoading ? (
                                <Skeleton width="10rem" />
                            ) : (
                                `displaying ${audiosDisplayed} out of ${audiosCount} audio${
                                    audiosCount !== 1 ? 's' : ''
                                }`
                            )}
                        </p>
                    </div>

                    <div className={styles.toolbar}>
                        <SearchInput
                            className={styles.search}
                            value={urlState.audiosSearch}
                            onChange={(search) => setUrlState({ audiosPage: 1, audiosSearch: search })}
                        />

                        <div className={styles.toolbarItem}>
                            <label className={styles.toolbarItemLabel} htmlFor="audiosOrdering">
                                Sort By
                            </label>
                            <Select
                                className={styles.orderingSelect}
                                styles={sortingSelectStyles}
                                id="audiosOrdering"
                                options={audiosOrderingOptions}
                                value={audiosOrderingOptions.find((o) => o.value === urlState.audiosOrdering)}
                                onChange={(o) =>
                                    setUrlState({ audiosOrdering: o?.value ?? initialUrlState['audiosOrdering'] })
                                }
                            />
                        </div>
                    </div>

                    <AudioPlayerProvider>
                        <TiktokAudiosTable
                            noDataLabel="No data found"
                            ordering={urlState.audiosOrdering as UrlState['audiosOrdering']}
                            loading={audiosLoading}
                            data={audios}
                            count={audiosCount}
                            page={Number(urlState.audiosPage)}
                            setPage={(page) => setUrlState({ audiosPage: page })}
                            pageSize={Number(urlState.audiosPageSize)}
                            setPageSize={(pageSize) =>
                                setUrlState({ audiosPageSize: pageSize ?? initialUrlState['audiosPageSize'] })
                            }
                            onPaginationChange={(state) => setAudiosDisplayed(state.pageRows?.length)}
                            onRowClick={(row) => {
                                setUrlState({ audioDetails: Number(row.music_id) });
                                setIsAudioDetailsModalOpen(true);
                            }}
                        />

                        <TiktokAudioModal
                            isModalOpen={isAudioDetailsModalOpen}
                            closeModal={() => {
                                setIsAudioDetailsModalOpen(false);
                                setUrlState({ audioDetails: null });
                            }}
                            audioId={Number(urlState.audioDetails)}
                        />
                    </AudioPlayerProvider>
                </div>
            </Card>
        </>
    );
};

export default SongCollectionTracker;
